/**
 * Recording format negotiation (spec section 22).
 *
 * Hard rule: never lie about the container. If the browser records WebM,
 * the file is named .webm and the UI says WebM. The studio negotiates at
 * runtime with MediaRecorder.isTypeSupported() rather than assuming a
 * browser version.
 */

export const RECORDING_CANDIDATES = [
  {
    mime: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    container: 'mp4',
    extension: 'mp4',
    videoCodec: 'H.264 (avc1)',
    audioCodec: 'AAC',
    label: 'MP4 / H.264 + AAC',
    preferred: true,
  },
  {
    mime: 'video/mp4;codecs=h264,aac',
    container: 'mp4',
    extension: 'mp4',
    videoCodec: 'H.264',
    audioCodec: 'AAC',
    label: 'MP4 / H.264 + AAC (alias)',
    preferred: true,
  },
  {
    mime: 'video/mp4',
    container: 'mp4',
    extension: 'mp4',
    videoCodec: 'browser default',
    audioCodec: 'browser default',
    label: 'MP4 (browser default codecs)',
    preferred: false,
  },
  {
    mime: 'video/webm;codecs=vp9,opus',
    container: 'webm',
    extension: 'webm',
    videoCodec: 'VP9',
    audioCodec: 'Opus',
    label: 'WebM / VP9 + Opus',
    preferred: false,
  },
  {
    mime: 'video/webm;codecs=vp8,opus',
    container: 'webm',
    extension: 'webm',
    videoCodec: 'VP8',
    audioCodec: 'Opus',
    label: 'WebM / VP8 + Opus',
    preferred: false,
  },
  {
    mime: 'video/webm',
    container: 'webm',
    extension: 'webm',
    videoCodec: 'browser default',
    audioCodec: 'browser default',
    label: 'WebM (browser default codecs)',
    preferred: false,
  },
];

/** Default probe: ask the real MediaRecorder. */
export function defaultSupportProbe() {
  return (mime) => {
    try {
      return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime);
    } catch {
      return false;
    }
  };
}

/**
 * @returns {{
 *   supported: Array, unsupported: Array, chosen: object|null,
 *   container: string|null, extension: string|null, canRecord: boolean,
 *   warning: string|null
 * }}
 */
export function selectRecordingFormat(isSupported = defaultSupportProbe()) {
  const supported = [];
  const unsupported = [];
  for (const candidate of RECORDING_CANDIDATES) {
    (isSupported(candidate.mime) ? supported : unsupported).push(candidate);
  }

  // Prefer MP4 when the browser genuinely supports it, otherwise WebM.
  const chosen = supported.find((c) => c.container === 'mp4') ?? supported[0] ?? null;

  let warning = null;
  if (!chosen) {
    warning = 'This browser does not support MediaRecorder recording from a canvas stream. Export is unavailable.';
  } else if (chosen.container === 'webm') {
    warning =
      'This browser cannot record MP4 from a canvas. The export will be a real WebM file (VP8/VP9 + Opus) - the file name and the UI will say WebM, never MP4.';
  }

  return {
    supported,
    unsupported,
    chosen,
    container: chosen?.container ?? null,
    extension: chosen?.extension ?? null,
    canRecord: Boolean(chosen),
    warning,
  };
}

/** Human-readable summary used by the Settings and Export panels. */
export function describeRecordingSupport() {
  const result = selectRecordingFormat();
  return {
    ...result,
    summary: result.chosen
      ? `Recording: ${result.chosen.label}`
      : 'Recording: unavailable in this browser',
    supportedLabels: result.supported.map((c) => c.label),
  };
}

export function isWebCodecsAvailable() {
  return typeof globalThis.VideoEncoder === 'function' && typeof globalThis.AudioEncoder === 'function';
}

/**
 * Honest file naming. `base` must not include an extension.
 */
export function buildExportFilename(base, extension) {
  const safe = String(base || 'bex-cast')
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `${safe || 'bex-cast'}.${extension}`;
}

/** Report the actual container of a recorded blob, defensively. */
export function detectContainerFromMime(mime) {
  const value = String(mime ?? '').toLowerCase();
  if (value.includes('mp4')) return 'mp4';
  if (value.includes('webm')) return 'webm';
  if (value.includes('ogg')) return 'ogg';
  return 'unknown';
}
